import {Partner_Client, PublicClient, SMG_Client, smgAuthWorldpay, smgTransactorWorldpay} from "../constants";
import {
  AcceptBetRequestSchema, AcceptTncRequestSchema, ActivatePromoRequestSchema, AdjustBetRequestSchema, ChangeType,
  BetResolutionRequestSchema, CheckUserEmailExistsRequestSchema, ConfirmEmailChangeRequestSchema,
  ConfirmEmailRequestSchema, ContactCsrRequestSchema, CreatePrivateTournamentRequestSchema, GetBetsRequestSchema,
  DeactivateAccountRequestSchema, DoResetPinRequestSchema, EnableAutoPlayRequestSchema, GetPromosRequestSchema,
  EnableSelfLimitationRequest_TimeFrame, EnableSelfLimitationRequestSchema, GetTournamentPrizeDistributionRequestSchema,
  GetTournamentRequestSchema, GetTournamentScheduleRequestSchema, GetTransactionHistoryRequestSchema, OtpMethod,
  JoinPrivateTournamentRequestSchema, JoinTournamentRequestSchema, LeaveTournamentRequestSchema, Session, SessionSchema,
  ListDefaultTournamentRequestSchema, ListTournamentRequest_Sort, ListTournamentRequest_TournamentAccess,
  ListTournamentRequestSchema, RequestChangeRequestSchema, RequestEmailChangeRequestSchema, RequestOtpCodeRequestSchema,
  RequestResetPinRequestSchema, ResendEmailConfirmationRequestSchema, StartVeriffKycVerificationRequestSchema,
  SubmitPhotoRequestSchema, TournamentState, TournamentSurveyAnswerSchema, UpdatePhoneNumberRequestSchema,
  UpdateUserRequestSchema, VerifyOtpCodeRequestSchema, VerifyPrivateTournamentCodeRequestSchema
} from "../../generated/smg_service_pb";
import axios, {AxiosResponse} from "axios";
import {
  AcceptBetResult, DepositPayloadType, DepositResponse, PayoffMethodsResponse, PayoutVerificationPayload,
  PayoutVerificationResponse, RawPayoffResponse, ResendPayoutVerificationResponse, FinixInstrumentType,
  PaysafeInstrumentType, SaveCardPayloadType, SavedCardType, updateUserPayloadType, VerifiedPayoffV2Payload,
  WorldpayReply
} from "../types";
import {convertToUnixTimestamp, setVenueIdOrLocation} from "./index";
import {Buffer} from "buffer";
import {create} from "@bufbuild/protobuf";
import {
  DisableConfirmationBypassRequestSchema, EnableConfirmationBypassRequestSchema, FullSwingLoginCheckInRequestSchema,
  FullSwingLoginCheckOutRequestSchema, FullSwingSessionParticipationConfirmationDisplayedRequestSchema,
  FullSwingSessionParticipationConfirmationRespondRequestSchema, FullSwingSetPlayerBypassConfirmationRequestSchema,
  GetConfirmationBypassStatusRequestSchema, PartnerKioskUpdatesStreamRequestSchema, SimulateUserTemporaryRequestSchema
} from "../../generated/partner_service_pb";
import {StreamTournamentUpdatesRequestSchema} from "../../generated/smg_public_api_pb";
import {
  AcceptBetRequestSchema as BayAcceptBetRequestSchema, CancelBetRequestSchema as BayCancelBetRequestSchema,
  ResizeWagerRequestSchema, SetWagerModeRequestSchema
} from "../../generated/player_bay_session_pb";
import {
  SetNextWagerCurrencyRequestSchema, SwitchWagerCurrencyRequestSchema, WagerMode
} from "../../generated/bay_session_shared_pb";
import {PlayerBaySession_Client} from "../constants";
import * as Crypto from "expo-crypto";
import {normalizeSurveyAnswersForRequest} from "./surveyAnswers";

const Sort = ListTournamentRequest_Sort;
const moneyMovementRequestTimeoutMs = 30000;
/**
 * The two requests that wait on Finix, which paygate now allows 3s + 60s per call (SMG-4797): card save
 * and the payout itself. Normal Finix calls answer in under a second; these cover one that stalls. The
 * paygate-transactor ingress must allow as long (proxy-read-timeout, set per environment in
 * evenplay-deployments; nginx's default is 60s). Giving up sooner reports a failure (or "could not
 * confirm your withdrawal status") for a request paygate then completes correctly.
 */
const finixBoundRequestTimeoutMs = 120000;

export const coerceOptionalTournamentId = (id?: number | string) => {
  if (id == null || id === '') return undefined;
  const tournamentId = Number(id);
  return Number.isFinite(tournamentId) ? tournamentId : undefined;
};

// SMG-4033: partner-service fails in-band ({success: false, error}). Codes outside the allow-list are not trusted to
// be player-safe (20030099 carries the raw err.Error() text), so only the text of a validation error is kept; the
// list is deliberately minimal. Otherwise the message is empty, which means the caller shows its own copy, so callers
// must check it for truthiness, not with `??`. The raw code and text go to the console only. Used by Respond only: the
// caller of Displayed just logs its rejection.
const PARTNER_ERR_VALIDATION = 20030005; // partner-service constants/error.go ErrValidation, keep in lockstep

const toConfirmationError = (error?: {code: bigint, message: string}) => {
  console.warn('[ServiceClient] legacy confirmation rejected', {code: error?.code?.toString(), message: error?.message});
  // Number() tolerates a bigint, number or string code, whichever way the client decodes int64.
  const isValidationError = Number(error?.code) === PARTNER_ERR_VALIDATION;
  return new Error(isValidationError ? error?.message ?? '' : '');
};

const ServiceClient = {
  client: SMG_Client,
  public: PublicClient,
  partner_client: Partner_Client,
  session: create(SessionSchema, {}) as Session,
  payGateToken: '',
  async checkUserEmailExists(email: string) {
    const request = create(CheckUserEmailExistsRequestSchema, {emailAddress: email});

    return this.client.checkUserEmailExists(request).then(res => {
      const {success, error, exists} = res;
      if (success) return exists;
      else throw error;
    });
  },
  async requestOtpCode(val: { email?: string, phone?: string }) {
    const {email, phone} = val;
    const request = create(RequestOtpCodeRequestSchema, {});

    if (email) {
      request.emailAddress = email;
      request.preferredOtpMethod = OtpMethod.EMAIL;

    } else if (phone) {
      request.phoneNumber = phone;
      request.preferredOtpMethod = OtpMethod.SMS;
    }

    return this.client.requestOtpCode(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      else throw error;
    });
  },
  async verifyOtpCode(code: string, email: string, phone?: string) {
    const request = create(VerifyOtpCodeRequestSchema, {otpCode: code, emailAddress: email});
    if (phone) request.phoneNumber = phone;

    return this.client.verifyOtpCode(request).then(res => {
      const {success, error, codeIsCorrect} = res;

      if (success) return codeIsCorrect;
      else throw error;
    });
  },
  async deposit(data: DepositPayloadType): Promise<DepositResponse | undefined> {
    const config = {
      headers: {Authorization: `Bearer ${this.payGateToken}`},
      timeout: moneyMovementRequestTimeoutMs
    };

    return axios.post(`${smgTransactorWorldpay}/v1/transactions/deposit`, data, config).then((response: AxiosResponse<WorldpayReply<DepositResponse>>) => {
      const {success, message, result} = response.data;

      if (success) return result;
      else throw {message};
    });
  },
  async validateMerchant(verificationURL: string) {
    const config = {headers: {Authorization: `Bearer ${this.payGateToken}`}};

    return axios.post(`${smgTransactorWorldpay}/v1/transactions/deposit/applepay/verification`, {verificationURL}, config).then((response: AxiosResponse<WorldpayReply<any>>) => {
      const {success, message, result} = response.data;

      if (success) return result;
      else throw {message};
    });
  },
  async getPayGateToken() {
    const data = {sessionToken: this.session?.token || ''};
    const config = {timeout: moneyMovementRequestTimeoutMs};

    return axios.post(`${smgAuthWorldpay}/auth/v1/token`, data, config).then((response: AxiosResponse<WorldpayReply<any>>) => {
      const {success, result, message} = response.data;

      if (success && result) {
        this.payGateToken = result.accessToken;
        return result.accessToken;

      } else throw {message: message || 'Error'};
    });
  },
  async getSavedCards() {
    const config = {
      headers: {Authorization: `Bearer ${this.payGateToken}`}, timeout: moneyMovementRequestTimeoutMs
    };
    return axios.get(`${smgTransactorWorldpay}/v1/cards/getSavedCards`, config).then((response: AxiosResponse<WorldpayReply<SavedCardType[]>>) => {
      const {success, result, message} = response.data;

      if (success && result) return result;
      else throw {message: message || 'Error'};
    });
  },
  async saveCard(data: SaveCardPayloadType, payGateToken: string) {
    const config = {
      headers: {Authorization: `Bearer ${payGateToken}`}, timeout: moneyMovementRequestTimeoutMs
    };

    return axios.post(`${smgTransactorWorldpay}/v1/cards/save`, data, config).then((response: AxiosResponse<WorldpayReply<SavedCardType>>) => {
      const {success, result, message} = response.data;

      if (success && result) return result;
      else throw {message: message || 'Error'};
    });
  },
  /**
   * Register a Finix-tokenized card as a payout destination (SMG-4557).
   *
   * No billing address is sent, and none is needed: paygate resolves it from the user's KYC record
   * and REFUSES to mint the identity when that address is incomplete. Sending client fields could
   * not help anyway — the refusal gates on the KYC value alone, precisely so a caller cannot
   * satisfy it with self-asserted data.
   *
   * `phone` is sent because paygate has no other source for it — the JWT principal carries none —
   * and Finix's Sandbox Certification requires a phone on the Identity. The identity is
   * **write-once**: paygate creates one only when the user has no `finixIdentityId`, and every
   * later save reuses it. A phone omitted on the first call is therefore permanent, not late.
   */
  async saveFinixInstrument(finixToken: string, phone: string, payGateToken: string): Promise<FinixInstrumentType> {
    const config = {
      headers: {Authorization: `Bearer ${payGateToken}`}, timeout: finixBoundRequestTimeoutMs
    };

    return axios.post(`${smgTransactorWorldpay}/v1/cards/finix/instrument`, {
      finixToken, instrumentType: 'CARD', phone
    }, config).then((response: AxiosResponse<WorldpayReply<FinixInstrumentType>>) => {
      const {success, result, message} = response.data;

      if (success && result) return result;
      else throw {message: message || 'Error'};
    });
  },
  /**
   * Register a Paysafe-tokenized card as a payout destination (SMG-4057).
   *
   * `paymentHandleToken` is the SINGLE-USE token from Paysafe JS; paygate converts it to a
   * multi-use handle on the user's Paysafe customer and answers with the opaque `paysafe:<id>`
   * destination ref. As with Finix, no billing address is sent — paygate resolves it from KYC.
   */
  async savePaysafeInstrument(paymentHandleToken: string, payGateToken: string): Promise<PaysafeInstrumentType> {
    const config = {
      headers: {Authorization: `Bearer ${payGateToken}`}, timeout: moneyMovementRequestTimeoutMs
    };

    return axios.post(`${smgTransactorWorldpay}/v1/cards/paysafe/instrument`, {
      paymentHandleToken
    }, config).then((response: AxiosResponse<WorldpayReply<PaysafeInstrumentType>>) => {
      const {success, result, message} = response.data;

      if (success && result) return result;
      else throw {message: message || 'Error'};
    });
  },
  /**
   * The user's saved Paysafe payout card, if any (SMG-4057), so the withdraw flow can offer it
   * instead of asking for the card again. Read alongside getSavedCards, with the same session.
   */
  async getPaysafeInstrument(): Promise<PaysafeInstrumentType> {
    const config = {
      headers: {Authorization: `Bearer ${this.payGateToken}`}, timeout: moneyMovementRequestTimeoutMs
    };

    return axios.get(`${smgTransactorWorldpay}/v1/cards/paysafe/instrument`, config).then((response: AxiosResponse<WorldpayReply<PaysafeInstrumentType>>) => {
      const {success, result, message} = response.data;

      if (success && result) return result;
      else throw {message: message || 'Error'};
    });
  },
  /**
   * The user's saved Finix payout card, if any (SMG-4797). A Finix instrument is not a SavedCard, so
   * getSavedCards never returns it; without this the withdraw flow lost it on reopening. Read
   * alongside getSavedCards, with the same session. A user has one Finix instrument, replaced by
   * each save.
   */
  async getFinixInstrument(): Promise<FinixInstrumentType> {
    const config = {
      headers: {Authorization: `Bearer ${this.payGateToken}`}, timeout: moneyMovementRequestTimeoutMs
    };

    return axios.get(`${smgTransactorWorldpay}/v1/cards/finix/instrument`, config).then((response: AxiosResponse<WorldpayReply<FinixInstrumentType>>) => {
      const {success, result, message} = response.data;

      if (success && result) return result;
      else throw {message: message || 'Error'};
    });
  },
  async deleteCard(cardUid: string) {
    const config = {headers: {Authorization: `Bearer ${this.payGateToken}`}};
    const data = {cardUid};

    return axios.post(`${smgTransactorWorldpay}/v1/cards/delete`, data, config).then((res: AxiosResponse<WorldpayReply<any>>) => {
      const {success, result, message} = res.data;

      if (success) return result;
      else {
        console.log('deleteCard res.data', res.data);
        throw {message: message || 'Error'};
      }
    });
  },
  async payoffV2(data: VerifiedPayoffV2Payload, idempotencyKey: string, payGateToken: string): Promise<RawPayoffResponse> {
    const config = {
      headers: {Authorization: `Bearer ${payGateToken}`, 'Idempotency-Key': idempotencyKey},
      timeout: finixBoundRequestTimeoutMs
    };

    return axios.post(`${smgTransactorWorldpay}/v2/transactions/payoff`, data, config).then((res: AxiosResponse<WorldpayReply<RawPayoffResponse>>) => {
      const {success, message, result} = res.data;

      if (success) return result || {};
      else throw {message: message || 'Error'};
    });
  },
  async startPayoffVerification(data: PayoutVerificationPayload, idempotencyKey: string, payGateToken: string): Promise<PayoutVerificationResponse> {
    const config = {
      headers: {Authorization: `Bearer ${payGateToken}`, 'Idempotency-Key': idempotencyKey},
      timeout: moneyMovementRequestTimeoutMs
    };

    return axios.post(`${smgTransactorWorldpay}/v2/transactions/payoff/verification`, data, config).then((res: AxiosResponse<WorldpayReply<PayoutVerificationResponse>>) => {
      const {success, message, result} = res.data;

      if (success && result) return result;
      else throw {message: message || 'Error'};
    });
  },
  async resendPayoffVerification(verificationId: string, payGateToken: string): Promise<ResendPayoutVerificationResponse> {
    const config = {
      headers: {Authorization: `Bearer ${payGateToken}`},
      timeout: moneyMovementRequestTimeoutMs
    };

    return axios.post(`${smgTransactorWorldpay}/v2/transactions/payoff/verification/${verificationId}/resend`, {}, config).then((res: AxiosResponse<WorldpayReply<ResendPayoutVerificationResponse>>) => {
      const {success, message, result} = res.data;

      if (success) return result || {};
      else throw {message: message || 'Error'};
    });
  },
  async getPayoffMethodsV2(amount: number) {
    const config = {
      headers: {Authorization: `Bearer ${this.payGateToken}`}, params: {amount}, timeout: moneyMovementRequestTimeoutMs
    };

    return axios.get(`${smgTransactorWorldpay}/v2/transactions/payoff/methods`, config).then((res: AxiosResponse<WorldpayReply<PayoffMethodsResponse>>) => {
      const {success, message, result} = res.data;

      if (success && result) return result;
      else throw {message: message || 'Error'};
    });
  },
  async sale(data: { amount: number, location: { latitude: string, longitude: string } }) {
    const config = {headers: {Authorization: `Bearer ${this.payGateToken}`}};

    return axios.post(`${smgTransactorWorldpay}/v1/transactions/sale`, data, config).then((response: AxiosResponse<WorldpayReply<any>>) => {
      const {success, result, message} = response.data;

      if (success && result?.transactionId) return result.transactionId;
      else throw {message: message || 'Error'};
    });
  },
  async enableSelfLimitation(timeFrame: EnableSelfLimitationRequest_TimeFrame) {
    const request = create(EnableSelfLimitationRequestSchema, {token: this.session.token || '', timeFrame});
    await setVenueIdOrLocation(request);

    return this.client.enableSelfLimitation(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      throw error;
    });
  },
  async deactivateAccount() {
    const request = create(DeactivateAccountRequestSchema, {token: this.session.token || ''});

    return this.client.deactivateAccount(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      else throw error;
    });
  },
  async acceptTnc() {
    const request = create(AcceptTncRequestSchema, {token: this.session.token || ''});

    return this.client.acceptTnc(request).then(res => {
      const {error, success} = res;

      if (success) return success;
      else throw error;
    });
  },
  /**
   * @deprecated SMG-3006 — legacy smg-core Bet surface.
   *
   * FullSwing should stay stream-driven through Context.bayActiveBetId.
   * BetSlip only uses this call as a transitional one-shot fetch for the
   * legacy Bet shape (challenges + paylines) after bay-session signals a
   * change.
   *
   * non-FullSwing has no bay-session player-state stream yet, so BetSlip
   * still uses a focused refresh path: refresh Context.user, then fetch the
   * active Bet after that user update reaches the screen.
   *
   * StoreContextProvider.logout uses this for the legacy refund path and as a
   * read-only fallback when a bay-session PlayerState has not projected the
   * active bet before logout. Bay-session cancellation still goes through
   * CancelBet. Do not add new callers without checking both FullSwing and
   * non-FullSwing behavior.
   */
  async getBets(state: 'open' | 'accepted', localSession?: Session) {
    const request = create(GetBetsRequestSchema, {
      token: localSession?.token || this.session.token || '',
      betState: state,
    });

    return this.client.getBets(request).then(res => {
      const {success, error, bets} = res;

      if (success) return bets;
      else throw error;
    });
  },
  async enableAutoPlay(enabled: boolean, betAmount: number) {
    const request = create(EnableAutoPlayRequestSchema, {token: this.session.token || '', enabled, betAmount});

    return this.client.enableAutoPlay(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      else throw error;
    });
  },
  async updateUser(value: updateUserPayloadType) {
    const {
      allowPartnerPromos, allowEmailNotifications, allowTextNotifications, username, handicap, handedness, firstname,
      lastname, dateOfBirth, gender
    } = value;

    const request = create(UpdateUserRequestSchema, {
      token: this.session.token || '',
      username,
      allowTextNotifications,
      allowEmailNotifications,
      allowPartnerPromos,
      handicap,
      handedness,
      firstName: firstname,
      lastName: lastname,
      gender,
    });
    if (dateOfBirth) request.dateOfBirth = convertToUnixTimestamp(dateOfBirth);
    // await setVenueIdOrLocation(request);

    return this.client.updateUser(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      else throw error;
    });
  },
  async requestEmailChange(email: string) {
    const request = create(RequestEmailChangeRequestSchema, {
      newEmail: email,
      token: this.session.token || '',
    });

    return this.client.requestEmailChange(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      else throw error;
    });
  },
  async confirmEmail(token: string) {
    const request = create(ConfirmEmailRequestSchema, {confirmationToken: token});

    return this.client.confirmEmail(request).then(res => {
      const {success, error, session} = res;

      if (success) return session;
      else throw error;
    });
  },
  async confirmEmailChange(token: string) {
    const request = create(ConfirmEmailChangeRequestSchema, {token});

    return this.client.confirmEmailChange(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      else throw error;
    });
  },
  async updatePhoneNumber(phone: string, code?: string) {
    const request = create(UpdatePhoneNumberRequestSchema, {
      token: this.session.token || '',
      phoneNumber: phone,
    });
    if (code) request.otpCode = code;

    return this.client.updatePhoneNumber(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      else throw error;
    });
  },
  async requestChange(type: ChangeType) {
    const request = create(RequestChangeRequestSchema, {
      token: this.session.token || '',
      changeType: type,
    });

    return this.client.requestChange(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      else throw error;
    });
  },
  async getPromos(player_id: number) {
    const request = create(GetPromosRequestSchema, {
      token: this.session.token || '',
      playerId: BigInt(player_id),
    });

    return this.client.getPromos(request).then(res => {
      const {success, error, promos} = res;

      if (success) return promos;
      else throw error;
    });
  },
  async listTournaments(state: TournamentState[], sort: ListTournamentRequest_Sort, access: ListTournamentRequest_TournamentAccess[], mineOnly: boolean, showLastTwoDaysForPastTournaments = true) {
    const request = create(ListTournamentRequestSchema, {
      token: this.session.token || '',
      tournamentAccess: access,
      mineOnly,
      states: state,
      sort,
    });

    if (showLastTwoDaysForPastTournaments && (!state.length && sort === Sort.DEFAULT)) request.showLastTwoDaysForPastTournaments = true;

    return this.client.listTournaments(request).then(res => {
      const {success, tournaments, error} = res;

      if (success) {
        return tournaments.filter(el => {
          const isCancelled = el.state === TournamentState.FINISHED || el.state === TournamentState.CANCELLED || el.state === TournamentState.VOIDED;

          if (isCancelled) {
            return typeof el.currentPlayerDetails !== 'undefined';

          } else {
            return true;
          }
        });

      } else {
        throw error;
      }
    });
  },
  async getTournament(id?: number | string) {
    const request = create(GetTournamentRequestSchema, {token: this.session.token || ''});
    const tournamentId = coerceOptionalTournamentId(id);
    if (tournamentId != null) request.tournamentId = tournamentId;

    return this.client.getTournament(request).then(res => {
      const {success, error, tournament} = res;

      if (success) return tournament;
      else throw error;
    });
  },
  async joinTournament(id: number, betEntreeFee: number, answers: Record<string, string[]>, enter_with_ticket: boolean = false) {
    const request = create(JoinTournamentRequestSchema, {
      token: this.session.token || '', tournamentId: id, betEntreeFee, enterWithTicket: enter_with_ticket
    });

    if (Object.keys(answers).length) {
      const surveyAnswers = normalizeSurveyAnswersForRequest(answers);
      if (surveyAnswers.length) {
        request.surveyAnswers = surveyAnswers.map(({questionId, response}) =>
          create(TournamentSurveyAnswerSchema, {questionId, response})
        );
      }
    }

    const res = await this.client.joinTournament(request);
    const {success, error} = res;

    if (success) return success;
    throw error;
  },
  async leaveTournament(id: number) {
    const request = create(LeaveTournamentRequestSchema, {
      tournamentId: id,
      token: this.session.token || '',
    });

    return this.client.leaveTournament(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      else throw error;
    });
  },
  /**
   * @deprecated SMG-3006 — legacy smg-core AcceptBet. Use acceptBetV2
   * (bay-session AcceptBet) for new callers. The legacy path assumes
   * engine pre-created a bet in 'open' state and the mobile flips it
   * to 'accepted'; bay-session creates the bet server-side in
   * 'accepted' state directly. Retained only for any non-migrated
   * callers; grep before adding new ones.
   */
  async acceptBet(betId: bigint, amount: number, challengeId?: bigint) {
    const request = create(AcceptBetRequestSchema, {token: this.session.token || '', betId, betAmount: BigInt(amount)});
    if (challengeId) request.challengeId = challengeId;

    return this.client.acceptBet(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      else throw error;
    });
  },
  /**
   * bay-session AcceptBet (SMG-3006). Replaces the legacy
   * smg-core acceptBet path. The legacy flow assumed engine pre-created
   * a bet in 'open' state and the mobile flipped it to 'accepted' by
   * supplying the existing betId; bay-session's AcceptBet creates the
   * smg_bet row server-side in 'accepted' state and returns the newly
   * assigned betId — there is no 'open' state in the BSA lifecycle.
   *
   * Caller MUST pass stationId from userSessionData.currentStationId and
   * the engine-supplied challengeId. The currency defaults to USD for
   * the FullSwing partner; bay-session enforces ONLY the literal wallet
   * vocabulary (USD / TGP / FSP / TGPTS / FSPTS / PRACTICE) at this
   * layer. Currency is the wallet identifier; the per-player wager
   * mode CATEGORY (NONE / MONEY / POINTS / PRACTICE — SMG-3087) is a
   * separate enum surfaced on SessionPlayer.
   *
   * Returns {betId, betState, betAmount, currency, wasIdempotentReplay}
   * so callers can update local activeBet state without a follow-up
   * GetBets round-trip. Server emits PLAYER_BET_ACCEPTED on the
   * per-user stream; useStreamPlayerState picks it up within one
   * frame and the BetSlip re-render proceeds without polling.
   *
   * idempotencyKey is generated client-side as a UUID v4 via
   * expo-crypto's Crypto.randomUUID(). Expo SDK 49 ships expo-crypto
   * with a native Android/iOS implementation backed by the platform's
   * SecRandomCopyBytes / SecureRandom, and a Web Crypto implementation
   * on the web target — no polyfill required, no react-native-get-
   * random-values dependency, no Hermes-version skew. The server
   * dedups against (user_id, accept_idempotency_key) so a retry with
   * the same key returns the original betId rather than creating a
   * duplicate.
   *
   * bayPlayerJwt is REQUIRED and must be Context.bayPlayerJwt, the same
   * bay-session-issued player JWT every other PlayerBaySessionService call
   * on this client carries. The transport (grpc/transport.ts) attaches no
   * interceptor, so a call that does not set this header explicitly sends
   * no credential at all and bay-session's authz interceptor denies it with
   * ErrTokenEmpty -> "[unauthenticated] authz: token is empty". This method
   * shipped without the header and every accept on a bay-session-routed
   * station failed that way in production (SMG-4464).
   */
  async acceptBetV2(
    stationId: bigint, challengeId: bigint, amount: number, currency: string, bayPlayerJwt: string
  ): Promise<AcceptBetResult> {
    // Fail here rather than round-tripping: the server's ErrTokenEmpty
    // surfaces to the player as an opaque modal, and callers reach this
    // before the FullSwingAuth handshake has populated Context. Mirrors the
    // non-empty gate useStreamPlayerState applies before opening its stream.
    if (!bayPlayerJwt) {
      throw new Error('Not signed in to this bay yet — please wait a moment and try again.');
    }
    const request = create(BayAcceptBetRequestSchema, {
      stationId, challengeId, betAmount: BigInt(amount), currency: currency || 'USD',
      acceptIdempotencyKey: Crypto.randomUUID()
    });
    const res = await PlayerBaySession_Client.acceptBet(request, {
      headers: {Authorization: `Bearer ${bayPlayerJwt}`},
    });

    return {
      betId: res.betId, betState: res.betState, betAmount: res.betAmount, currency: res.currency,
      wasIdempotentReplay: res.wasIdempotentReplay
    };
  },
  /**
   * bay-session CancelBet — the inverse of acceptBetV2 while the bet is still
   * accepted. The server transitions the bet to refunded without a wallet
   * operation and publishes the PlayerState snapshot that clears active_bet_id.
   */
  async cancelBetV2(betId: bigint, bayPlayerJwt: string) {
    const request = create(BayCancelBetRequestSchema, {betId: String(betId), idempotencyKey: Crypto.randomUUID()});
    return PlayerBaySession_Client.cancelBet(request, {headers: {Authorization: `Bearer ${bayPlayerJwt}`}});
  },
  /**
   * bay-session SetWagerMode (SMG-3447). Sets the caller's per-player
   * wager CATEGORY (MONEY / POINTS / PRACTICE / NONE) when there is NO
   * live wager. The concrete points wallet token (TGPTS / FSPTS) is
   * resolved server-side from venue_partner config — the client only
   * chooses the category, so this is the right call for the mobile
   * Real-Money/Points picker when the player has no open bet.
   *
   * Needs station_id + the bay-session player JWT (Context.bayPlayerJwt) —
   * the same auth surface as SetPlayerConfirmationSettings. The server
   * derives next_wager_currency from the category + venue config.
   */
  async setWagerMode(stationId: bigint, wagerMode: WagerMode, bayPlayerJwt: string) {
    const request = create(SetWagerModeRequestSchema, {stationId, wagerMode});
    return PlayerBaySession_Client.setWagerMode(request, {
      headers: {Authorization: `Bearer ${bayPlayerJwt}`},
    });
  },
  /**
   * bay-session ResizeWager (SMG-3140 / SMG-3488) — re-amount the caller's
   * CURRENT accepted wager. This is the correct post-BSA-cutover path for a
   * "Shot Amount" change, replacing the legacy smg-core SmgService.AdjustBet:
   * AdjustBet updated smg_bet only and published NO bay-session snapshot, so
   * the change never reached the kiosk/simulator (FSG-8821). ResizeWager
   * updates the bet amount inside a MutateStation tx AND publishes a fresh
   * SessionState, so the game re-fetches its paytable.
   *
   * Keyed by (caller from JWT, station_id, currency) — NOT bet_id. newAmount
   * is an ABSOLUTE value in minor units (> 0); the caller resolves any "max"
   * choice to a concrete amount before calling (the shot-amount modal already
   * does, via extendedUserData.maxShotAmount). idempotency_key is optional
   * (last-write-wins). Auth uses the bay-session player JWT, like setWagerMode.
   */
  async resizeWager(stationId: bigint, currency: string, newAmount: number, bayPlayerJwt: string) {
    const request = create(ResizeWagerRequestSchema, {
      stationId,
      currency: currency || 'USD',
      newAmount: BigInt(newAmount),
      idempotencyKey: Crypto.randomUUID(),
    });
    return PlayerBaySession_Client.resizeWager(request, {
      headers: {Authorization: `Bearer ${bayPlayerJwt}`},
    });
  },
  /**
   * bay-session SetNextWagerCurrency (SMG-3447). Sets the sticky
   * next_wager_currency wallet token explicitly (and the server derives
   * the matching category). Mobile chooses the CATEGORY, not the token,
   * so this is used only when the client knows the concrete token; the
   * category-only path goes through setWagerMode. Kept available for
   * completeness / the simulator surface.
   */
  async setNextWagerCurrency(stationId: bigint, currency: string, bayPlayerJwt: string) {
    const request = create(SetNextWagerCurrencyRequestSchema, {
      stationId, currency, idempotencyKey: Crypto.randomUUID(),
    });
    return PlayerBaySession_Client.setNextWagerCurrency(request, {
      headers: {Authorization: `Bearer ${bayPlayerJwt}`},
    });
  },
  /**
   * bay-session SwitchWagerCurrency (SMG-3447). Atomically re-mints the
   * caller's CURRENT open wager into target_currency (cancel + remint in
   * one server transaction — SMG-3443/3444). This is the call to use when
   * the player flips Real Money ↔ Points WITH a live/open wager: the
   * SMG-3233 flip block rejects a bare SetWagerMode in that state, but
   * SwitchWagerCurrency is the sanctioned exempt path.
   *
   * target_currency is the wallet token: 'USD' for Real Money; for Points
   * pass the station's points token if known, else '' so the server
   * resolves it from venue_partner config (the mobile generally does not
   * know the concrete TGPTS / FSPTS token).
   */
  async switchWagerCurrency(stationId: bigint, targetCurrency: string, bayPlayerJwt: string) {
    const request = create(SwitchWagerCurrencyRequestSchema, {
      stationId, targetCurrency, idempotencyKey: Crypto.randomUUID(),
    });
    return PlayerBaySession_Client.switchWagerCurrency(request, {
      headers: {Authorization: `Bearer ${bayPlayerJwt}`},
    });
  },
  async resendEmailConfirmation() {
    const request = create(ResendEmailConfirmationRequestSchema, {token: this.session.token || ''});

    return this.client.resendEmailConfirmation(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      else throw error;
    });
  },
  async doResetPin(token: string, newPin: string) {
    const request = create(DoResetPinRequestSchema, {token, newPin});
    await setVenueIdOrLocation(request);

    return this.client.doResetPin(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      else throw error;
    });
  },
  async requestResetPin(email: string) {
    const request = create(RequestResetPinRequestSchema, {emailAddress: email});
    await setVenueIdOrLocation(request);

    return this.client.requestResetPin(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      else throw error;
    });
  },
  async refundBet(betId: number | bigint) {
    const request = create(BetResolutionRequestSchema, {
      betId: typeof betId === 'bigint' ? betId : BigInt(betId),
      token: this.session.token || '',
    });

    return this.client.refundBet(request).then(res => {
      const {resolved, error} = res;

      if (resolved) return resolved;
      else throw error;
    });
  },
  async contactCsr(message: string) {
    const request = create(ContactCsrRequestSchema, {
      request: message,
      token: this.session.token || '',
    });
    await setVenueIdOrLocation(request);

    return this.client.contactCsr(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      else throw error;
    });
  },
  async submitPhoto(image: string): Promise<boolean> {
    const bytes = Uint8Array.from(Buffer.from(image, 'base64'));
    const request = create(SubmitPhotoRequestSchema, {image: bytes, token: this.session.token || ''});

    const {success, error, isRecognized} = await this.client.submitPhoto(request);

    if (success) {
      return isRecognized;
    }

    throw error || new Error('Failed to submit photo');
  },
  async startVeriffKycVerification() {
    const request = create(StartVeriffKycVerificationRequestSchema, {token: this.session.token || ''});

    return this.client.startVeriffKycVerification(request).then(res => {
      const {success, error, veriffUrl} = res;

      if (success) return veriffUrl;
      else throw error;
    });
  },
  async activatePromo(code: string) {
    const request = create(ActivatePromoRequestSchema, {
      token: this.session.token || '',
      promoCode: code,
    });

    return this.client.activatePromo(request).then(res => {
      const {success, error, activatedPromo} = res;

      if (success) return activatedPromo;
      else throw error;
    });
  },
  async createPrivateTournament(numberOfShots: number, maxParticipants: number, entreeFee: number) {
    const request = create(CreatePrivateTournamentRequestSchema, {
      token: this.session.token || '',
      numberOfShots,
      maxParticipants,
      entreeFee: entreeFee * 100,
    });

    return this.client.createPrivateTournament(request).then(res => {
      const {success, newTournamentCode, error} = res;

      if (success) return newTournamentCode;
      else throw error;
    });
  },
  async verifyPrivateTournamentCode(code: string) {
    const request = create(VerifyPrivateTournamentCodeRequestSchema, {
      token: this.session.token || '',
      code,
    });

    return this.client.verifyPrivateTournamentCode(request).then(res => {
      const {success, error, tournament} = res;

      if (success) return tournament;
      else throw error;
    });
  },
  async adjustBet(betId: bigint, amount: number) {
    const request = create(AdjustBetRequestSchema, {token: this.session.token || '', betId, betAmount: BigInt(amount)});

    return this.client.adjustBet(request).then(res => {
      const {success, error, betId} = res;
      if (success) return betId;
      else throw error;
    });
  },
  async joinPrivateTournament(code: string, answers: Record<string, string[]>) {
    const request = create(JoinPrivateTournamentRequestSchema, {
      token: this.session.token || '', code, betEntreeFee: 0
    });

    if (Object.keys(answers).length) {
      const surveyAnswers = normalizeSurveyAnswersForRequest(answers);
      if (surveyAnswers.length) {
        request.surveyAnswers = surveyAnswers.map(({questionId, response}) =>
          create(TournamentSurveyAnswerSchema, {questionId, response})
        );
      }
    }

    const res = await this.client.joinPrivateTournament(request);
    const {success, error, tournament} = res;

    if (success) return tournament;
    throw error;
  },
  async getTransactionHistory(userId: number) {
    // SMG-3881: the server derives the user from the session token (fail-closed);
    // userId is deprecated/ignored but still sent for wire compatibility.
    const request = create(GetTransactionHistoryRequestSchema, {
      token: this.session.token || '',
      userId: BigInt(userId),
    });

    return this.client.getTransactionHistory(request).then(res => {
      const {success, error, history} = res;

      if (success) return history;
      else throw error;
    });
  },
  async listDefaultTournaments() {
    const request = create(ListDefaultTournamentRequestSchema, {token: this.session.token || ''});

    return this.client.listDefaultTournaments(request).then(res => {
      const {success, error, tournaments} = res;

      if (success) return tournaments;
      else throw error;
    });
  },
  async fullSwingCheckIn(session_id: string) {
    const request = create(FullSwingLoginCheckInRequestSchema, {
      token: this.session.token || '',
      sessionId: session_id,
    });

    return this.partner_client.fullSwingCheckIn(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      else throw error;
    });
  },
  async fullSwingCheckOut(session_id: string) {
    const request = create(FullSwingLoginCheckOutRequestSchema, {
      token: this.session.token || '',
      sessionId: session_id,
    });

    return this.partner_client.fullSwingCheckOut(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      else throw error;
    });
  },
  async simulateUserTemporary(station_id: number) {
    const request = create(SimulateUserTemporaryRequestSchema, {
      token: this.session.token || '',
      stationId: BigInt(station_id),
    });

    return this.partner_client.simulateUserTemporary(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      else throw error;
    });
  },
  streamTournamentUpdates(tournamentId: number) {
    const request = create(StreamTournamentUpdatesRequestSchema, {tournamentId});

    return this.public.streamTournamentUpdates(request);
  },
  partnerKioskUpdatesStream(session_id: string | null) {
    const request = create(PartnerKioskUpdatesStreamRequestSchema, {
      token: this.session.token || '',
      sessionId: session_id || '',
    });

    return this.partner_client.partnerKioskUpdatesStream(request);
  },
  async fullSwingSessionParticipationConfirmationRespond(authSessionToken: string) {
    const request = create(FullSwingSessionParticipationConfirmationRespondRequestSchema, {
      token: authSessionToken, accepted: true,
    });

    return this.partner_client.fullSwingSessionParticipationConfirmationRespond(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      else throw toConfirmationError(error);
    });
  },
  async fullSwingSessionParticipationConfirmationDisplayed(sessionId: string, authSessionToken: string) {
    const request = create(FullSwingSessionParticipationConfirmationDisplayedRequestSchema, {
      token: authSessionToken, sessionId,
    });

    return this.partner_client.fullSwingSessionParticipationConfirmationDisplayed(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      else throw error;
    });
  },
  async getTournamentSchedule() {
    const request = create(GetTournamentScheduleRequestSchema, {token: this.session.token || ''});

    return this.client.getTournamentSchedule(request).then(res => {
      const {success, error, levels} = res;

      if (success) return levels;
      else throw error;
    })
  },
  async getTournamentPrizeDistribution(tournament_id?: number) {
    const request = create(GetTournamentPrizeDistributionRequestSchema, {token: this.session.token || ''});
    if (tournament_id) request.tournamentId = tournament_id;

    return this.client.getTournamentPrizeDistribution(request).then(res => {
      const {success, error, tournamentPrizeDistribution} = res;

      if (success) return tournamentPrizeDistribution;
      else throw error;
    });
  },
  async enableConfirmationBypass(authSessionToken: string): Promise<boolean> {
    const request = create(EnableConfirmationBypassRequestSchema, {token: authSessionToken});
    const response = await this.partner_client.enableConfirmationBypass(request);
    return response.enabled;
  },
  async disableConfirmationBypass(authSessionToken: string): Promise<boolean> {
    const request = create(DisableConfirmationBypassRequestSchema, {token: authSessionToken});
    const response = await this.partner_client.disableConfirmationBypass(request);
    return response.enabled;
  },
  async getConfirmationBypassStatus(authSessionToken: string): Promise<boolean> {
    const request = create(GetConfirmationBypassStatusRequestSchema, {token: authSessionToken});
    const response = await this.partner_client.getConfirmationBypassStatus(request);
    return response.enabled;
  },
  async setLegacyConfirmBeforeEachTurn(enabled: boolean, authSessionToken: string): Promise<boolean> {
    if (enabled) {
      await this.disableConfirmationBypass(authSessionToken);
    } else {
      await this.enableConfirmationBypass(authSessionToken);
    }
    return true;
  },
  async getLegacyConfirmBeforeEachTurn(authSessionToken: string): Promise<boolean> {
    return !(await this.getConfirmationBypassStatus(authSessionToken));
  },
  /**
   * The single-shot toggle's write path for the LEGACY-FALLBACK session
   * backend only.
   *
   * On a station bay-session does not manage (FullSwingAuth answered
   * STATION_NOT_BAY_SESSION_MANAGED at login), the app has no bay-session
   * player JWT, so the canonical write —
   * PlayerBaySessionService.SetPlayerConfirmationSettings — is unreachable.
   * This partner-service RPC is the fallback carrier: it authenticates with
   * the smg SESSION token (server-side act-on-self: the token must resolve
   * to `userId`), persists the durable per-shot preference
   * (smg_user_details.confirm_on_shot_pref = !enabled) that the server-side
   * confirmation gates enforce, and updates the legacy per-session display
   * column driving account_security_bypass_enabled on the kiosk stream.
   *
   * `enabled` speaks the legacy BYPASS vocabulary: enabled=true ⇔ skip
   * confirmation ⇔ single-shot OFF. Callers on the bay-session backend must
   * keep using SetPlayerConfirmationSettings directly — this exists only
   * because the fallback cohort has no other credential.
   */
  fullSwingSetPlayerBypassConfirmation(sessionId: string, userId: bigint, enabled: boolean): Promise<boolean> {
    const request = create(FullSwingSetPlayerBypassConfirmationRequestSchema, {
      sessionId, userId, enabled, token: this.session.token || '',
    });

    return this.partner_client.fullSwingSetPlayerBypassConfirmation(request).then(res => {
      const {success, error} = res;

      if (success) return success;
      else throw error;
    });
  }
};

export default ServiceClient;
